import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import Avatar from '@mui/material/Avatar';
import Tooltip from '@mui/material/Tooltip';
import { useMsal } from '@azure/msal-react';
import { ACCENT_COLOR, SURFACE_BG } from '@/constants/colors';
import { APP_VERSION } from '@/constants/app';

/**
 * NavBar — Top navigation strip.
 *
 * Contents (left → right):
 *  - Company logo (placeholder until real asset is provided)
 *  - "OptiBush" title
 *  - Spacer
 *  - Version badge
 *  - Feedback mailto button
 *  - Reset FAB (red, calls a global reset action — wired in Phase 10)
 */
const NavBar: React.FC = () => {
  const { accounts } = useMsal();

  const userName = accounts[0]?.name ?? '';
  const parts = userName.split(/[, ]+/).filter(Boolean);
  const initials = parts.map((s) => s[0]).join('').slice(0, 2).toUpperCase();
  // Format as "Last, First" for the tooltip
  const displayName =
    userName.includes(',') ? userName : parts.length >= 2
      ? `${parts.slice(1).join(' ')}, ${parts[0]}`
      : userName;

  return (
    <Card
      elevation={4}
      sx={{
        mx: '12px',
        mt: '12px',
        px: 1.5,
        py: 1,
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        borderRadius: '12px',
        bgcolor: SURFACE_BG,
        flexShrink: 0,
      }}
    >
      {/* Logo placeholder — replace src with real logo data-uri or URL */}
      <Box
        component="img"
        src="/assets/logo.png"
        alt="Company Logo"
        sx={{
          height: 32,
          width: 'auto',
          objectFit: 'contain',
          display: 'block',
          flex: '0 0 auto',
        }}
        onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
          // Hide broken image if logo not yet deployed
          (e.target as HTMLImageElement).style.display = 'none';
        }}
      />

      {/* Title */}
      <Typography
        sx={{
          fontWeight: 'bold',
          fontSize: 22,
          color: ACCENT_COLOR,
          userSelect: 'none',
        }}
      >
        OptiBush
      </Typography>

      {/* Spacer */}
      <Box sx={{ flex: 1 }} />
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          px: 0.5,
          py: 0.5,
          mb: 0.5,
          backgroundColor: '#FFF5F5',
          border: '1px solid #FECACA',
          borderLeft: '4px solid #DC2626',
          borderRadius: '6px',
        }}
      >
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: 13,
            lineHeight: 1.4,
            color: '#B91C1C',
            letterSpacing: '0.2px',
            userSelect: 'none',
          }}
        >
          ⚠️ THIS TOOL IS FOR TESTING PURPOSES ONLY. DO NOT USE OR RELY ON OUTPUTS FROM THIS TOOL.
        </Typography>
      </Box>

      {/* Spacer */}
      <Box sx={{ flex: 1 }} />

      {/* Version */}
      <Typography
        variant="body2"
        sx={{ fontWeight: 'bold', fontSize: 18 , color: '#6c757d', whiteSpace: 'nowrap', mr: 1 }}
      >
        {APP_VERSION}
      </Typography>

      {/* User profile */}
      <Tooltip title={displayName}>
        <Avatar
          alt={userName}
          sx={{ width: 32, height: 32, fontSize: 14, bgcolor: '#0B4566' , fontWeight: 'bold', color: '#fff', alignItems: 'center', justifyContent: 'center', lineHeight: 1}}
        >
          {initials}
        </Avatar>
      </Tooltip>
    </Card>
  );
};

export default NavBar;
